import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, Plus, Pencil, Archive, ArchiveRestore, ShieldCheck, ShieldOff, Search, ArrowLeft, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { useApi } from './DashboardLayout';

const DetailField = ({ label, value }) => (
  <div style={{ marginBottom: '0.75rem' }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>{label}</div>
    <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>{value || '-'}</div>
  </div>
);

export default function OffersPage({ userType, certFilter }) {
  const [offers, setOffers] = useState([]);
  const [search, setSearch] = useState('');
  const [filterCert, setFilterCert] = useState(certFilter || 'all');
  const [loading, setLoading] = useState(true);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [certDate, setCertDate] = useState('');
  const [certEndDate, setCertEndDate] = useState('');
  const rowRefs = useRef({});
  const navigate = useNavigate();
  const { apiFetch } = useApi();

  const isArchiveView = certFilter === 'archived';

  useEffect(() => { setFilterCert(certFilter || 'all'); loadOffers(); }, [certFilter]);

  const loadOffers = async () => {
    setLoading(true);
    const url = isArchiveView ? '/api/offers?include_archived=true' : '/api/offers';
    const r = await apiFetch(url);
    const d = await r?.json();
    if (d) setOffers(isArchiveView ? d.filter(o => o.archived) : d);
    setLoading(false);
  };

  const deleteOffer = async (id) => {
    if (!window.confirm('Angebot wirklich archivieren?')) return;
    await apiFetch(`/api/offers/${id}/archive`, { method: 'POST' });
    setSelectedIdx(null);
    loadOffers();
  };

  const restoreOffer = async (id) => {
    await apiFetch(`/api/offers/${id}/restore`, { method: 'POST' });
    loadOffers();
  };

  const certifyOffer = async (id) => {
    if (!certDate) return;
    await apiFetch(`/api/offers/${id}/certify`, {
      method: 'POST',
      body: JSON.stringify({ anerkannt_seit: certDate, anerkannt_bis: certEndDate })
    });
    setCertDate('');
    setCertEndDate('');
    loadOffers();
  };

  const revokeOffer = async (id) => {
    if (!window.confirm('Zertifizierung wirklich widerrufen?')) return;
    await apiFetch(`/api/offers/${id}/revoke`, { method: 'POST' });
    loadOffers();
  };

  const filtered = offers.filter(o => {
    const matchSearch = search === '' ||
      [o.provider_name, o.veranstaltungsort_name, o.angebot, o.bezirk, o.wochentag].some(v => (v || '').toLowerCase().includes(search.toLowerCase()));
    const matchCert = filterCert === 'all' || (filterCert === '1' ? o.certified === 1 : o.certified !== 1);
    return matchSearch && matchCert;
  });

  const goBack = () => {
    const lastIdx = selectedIdx;
    setSelectedIdx(null);
    setCertDate('');
    setCertEndDate('');
    if (lastIdx !== null) {
      setTimeout(() => {
        const id = filtered[lastIdx]?.id;
        if (id && rowRefs.current[id]) rowRefs.current[id].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
    }
  };

  const nav = (dir) => {
    const newIdx = selectedIdx + dir;
    if (newIdx >= 0 && newIdx < filtered.length) {
      setSelectedIdx(newIdx);
      setCertDate('');
      setCertEndDate('');
    }
  };

  const o = selectedIdx !== null ? filtered[selectedIdx] : null;

  // When entering detail view, pre-fill dates if already certified
  useEffect(() => {
    if (o) {
      setCertDate(o.anerkannt_seit || '');
      setCertEndDate(o.anerkannt_bis || '');
    }
  }, [selectedIdx]);

  // Detail View
  if (o) {
    return (
      <div data-testid="offers-page">
        <div className="detail-nav-bar">
          <button className="btn btn-secondary btn-sm" onClick={goBack} data-testid="offer-back-btn">
            <ArrowLeft size={14} /> Zurück zur Liste
          </button>
          <div className="detail-nav-pager">
            <button className="btn btn-secondary btn-sm" onClick={() => nav(-1)} disabled={selectedIdx === 0} data-testid="offer-prev-btn">
              <ChevronLeft size={14} /> Vorheriges
            </button>
            <span className="detail-nav-count">{selectedIdx + 1} / {filtered.length}</span>
            <button className="btn btn-secondary btn-sm" onClick={() => nav(1)} disabled={selectedIdx === filtered.length - 1} data-testid="offer-next-btn">
              Nächstes <ChevronRight size={14} />
            </button>
          </div>
        </div>

        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2>{o.provider_name || 'Angebot Details'}</h2>
            <p>{o.angebot || '-'} &middot; {o.bezirk || '-'} &middot; {o.wochentag || '-'}</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {!isArchiveView && <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/app/offers/${o.id}/edit`)} data-testid="offer-edit-detail"><Pencil size={14} /> Bearbeiten</button>}
            {!isArchiveView && userType <= 2 && (
              <button className="btn btn-secondary btn-sm" onClick={() => deleteOffer(o.id)} data-testid="offer-archive-detail"><Archive size={14} /> Archivieren</button>
            )}
            {isArchiveView && (
              <button className="btn btn-primary btn-sm" onClick={() => restoreOffer(o.id)} data-testid="offer-restore-detail"><ArchiveRestore size={14} /> Wiederherstellen</button>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div className="card">
            <div className="card-body">
              <h4 className="detail-section-title">Angebot</h4>
              <div className="grid-2">
                <DetailField label="Verein" value={o.provider_name} />
                <DetailField label="Angebot" value={o.angebot} />
                <DetailField label="Hauptindikation" value={o.hauptindikation} />
                <DetailField label="Rehabilitationssportart" value={o.rehabilitationssportart} />
                <DetailField label="Zusätzliche Bezeichnung" value={o.zusatzliche_bezeichnung} />
                <DetailField label="Positionsnummer" value={o.abrechenbare_positionsnummer} />
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <h4 className="detail-section-title">Veranstaltungsort & Zeit</h4>
              <div className="grid-2">
                <DetailField label="Veranstaltungsort" value={o.veranstaltungsort_name} />
                <DetailField label="Straße" value={o.veranstaltungsort_strasse} />
                <DetailField label="PLZ / Ort" value={`${o.veranstaltungsort_plz || ''} ${o.veranstaltungsort_ort || ''}`} />
                <DetailField label="Bezirk" value={o.bezirk} />
                <DetailField label="Wochentag" value={o.wochentag} />
                <DetailField label="Uhrzeit" value={`${o.uhrzeit_von || '-'} – ${o.uhrzeit_bis || '-'}`} />
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <h4 className="detail-section-title">Übungsleiter & Lizenz</h4>
              <div className="grid-2">
                <DetailField label="Übungsleiter" value={o.name_ubungsleiter} />
                <DetailField label="Lizenz" value={o.lizenz} />
                <DetailField label="Lizenz gültig bis" value={o.lizenz_gultig_bis} />
                <DetailField label="Notfallabsicherung" value={o.notfallabsicherung} />
              </div>
            </div>
          </div>

          {/* Certification Card - only for SA/Admin */}
          {!isArchiveView && userType <= 2 && (
            <div className="card" data-testid="cert-card">
              <div className="card-body">
                <h4 className="detail-section-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <ShieldCheck size={16} /> Zertifizierung
                </h4>

                {o.certified === 1 ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, padding: '8px 12px', background: '#dcfce7', borderRadius: 8 }}>
                      <ShieldCheck size={16} style={{ color: '#16a34a' }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#166534' }}>Zertifiziert</span>
                    </div>
                    <div className="grid-2">
                      <DetailField label="Anerkannt seit" value={o.anerkannt_seit} />
                      <DetailField label="Anerkannt bis" value={o.anerkannt_bis || 'Unbefristet'} />
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>Enddatum nachtragen / ändern</label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input type="date" className="form-input" value={certEndDate} onChange={e => setCertEndDate(e.target.value)} style={{ flex: 1, fontSize: 13, padding: '6px 10px' }} data-testid="cert-end-date-edit" />
                        <button className="btn btn-secondary btn-sm" data-testid="cert-update-end-btn"
                          onClick={async () => {
                            await apiFetch(`/api/offers/${o.id}/certify`, {
                              method: 'POST',
                              body: JSON.stringify({ anerkannt_seit: o.anerkannt_seit, anerkannt_bis: certEndDate })
                            });
                            loadOffers();
                          }}>
                          Speichern
                        </button>
                      </div>
                    </div>
                    <button className="btn btn-secondary btn-sm" onClick={() => revokeOffer(o.id)} style={{ marginTop: 16 }} data-testid="offer-revoke-detail">
                      <ShieldOff size={14} /> Zertifizierung widerrufen
                    </button>
                  </>
                ) : (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, padding: '8px 12px', background: '#fef9c3', borderRadius: 8 }}>
                      <Calendar size={16} style={{ color: '#a16207' }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#854d0e' }}>Nicht zertifiziert</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>Anerkannt seit *</label>
                        <input type="date" className="form-input" value={certDate} onChange={e => setCertDate(e.target.value)} style={{ fontSize: 13, padding: '6px 10px' }} data-testid="cert-date-input" />
                      </div>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>Anerkannt bis (optional)</label>
                        <input type="date" className="form-input" value={certEndDate} onChange={e => setCertEndDate(e.target.value)} style={{ fontSize: 13, padding: '6px 10px' }} data-testid="cert-end-date-input" />
                      </div>
                    </div>
                    {!certDate && (
                      <div style={{ fontSize: 12, color: '#b45309', marginBottom: 12 }}>Bitte das Datum "Anerkannt seit" eintragen, um die Zertifizierung durchzuführen.</div>
                    )}
                    <button className="btn btn-primary btn-sm" onClick={() => certifyOffer(o.id)} disabled={!certDate} data-testid="offer-certify-detail">
                      <ShieldCheck size={14} /> Zertifizieren
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // List View
  return (
    <div data-testid="offers-page">
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h2>Angebote</h2>
          <p>{offers.length} Herzgruppen-Angebote</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/app/offers/new')} data-testid="add-offer-btn"><Plus size={16} /> Neues Angebot</button>
      </div>

      <div className="card">
        <div className="card-body" style={{ paddingBottom: 0 }}>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="search-input" style={{ flex: 1 }}>
              <Search size={16} />
              <input className="form-input" placeholder="Suchen..." value={search} onChange={e => setSearch(e.target.value)} data-testid="offers-search" style={{ border: 'none', boxShadow: 'none', padding: '4px 0' }} />
            </div>
            {!isArchiveView && (
              <div style={{ display: 'flex', gap: 4 }}>
                {[['all', 'Alle'], ['1', 'Zertifiziert'], ['0', 'Nicht zertifiziert']].map(([v, l]) => (
                  <button key={v} className={`btn btn-sm ${filterCert === v ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilterCert(v)} data-testid={`filter-${v}`}>{l}</button>
                ))}
              </div>
            )}
          </div>
        </div>
        {loading ? (
          <div className="empty-state"><ClipboardList size={40} /><p>Wird geladen...</p></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state"><ClipboardList size={40} /><p>Keine Angebote gefunden</p></div>
        ) : (
          <div className="table-wrap">
            <table data-testid="offers-table">
              <thead><tr><th>Verein</th><th>Veranstaltungsort</th><th>Bezirk</th><th>Wochentag</th><th>Uhrzeit</th><th>Status</th></tr></thead>
              <tbody>
                {filtered.map((of, idx) => (
                  <tr key={of.id} ref={el => rowRefs.current[of.id] = el} data-testid={`offer-row-${of.id}`}
                    onClick={() => setSelectedIdx(idx)} style={{ cursor: 'pointer' }}>
                    <td style={{ fontWeight: 500 }}>{of.provider_name || '-'}</td>
                    <td>{of.veranstaltungsort_name || '-'}</td>
                    <td>{of.bezirk || '-'}</td>
                    <td>{of.wochentag || '-'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{of.uhrzeit_von || '-'} – {of.uhrzeit_bis || '-'}</td>
                    <td>
                      {of.deactivated && <span className="badge badge-dark" style={{ marginRight: 4 }}>Deaktiviert</span>}
                      {of.deregistration_date ? (
                        <span className="badge" style={{ background: '#FEF3C7', color: '#92400E' }}>Abmeldung {of.deregistration_date.split('-').reverse().join('.')}</span>
                      ) : of.certified === 1 ? (
                        <span className="badge badge-success"><span className="badge-dot green" /> Zertifiziert</span>
                      ) : (
                        <span className="badge badge-warning"><span className="badge-dot yellow" /> Offen</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <div style={{ padding: '0.75rem 1.5rem', borderTop: '1px solid var(--border)', fontSize: 13, color: 'var(--text-secondary)' }}>
            Zeige {filtered.length} von {offers.length} Angeboten
          </div>
        )}
      </div>
    </div>
  );
}
